import { Country, Region } from '@global/types/types';
import { AWS_COUNTRIES_URL } from '@global/App/constants/constants';

const NORMALIZED_COUNTRIES_CONDITIONS: Record<
  Country['isoCodeTwo'],
  Partial<Country>
> = {
  CW: {
    name: 'Curaçao',
  },
};

const normalizeCountries = (countries: Country[]) => {
  return countries.map((country) => {
    const normalizedValues =
      NORMALIZED_COUNTRIES_CONDITIONS[country.isoCodeTwo];
    if (!normalizedValues) return country;

    return {
      ...country,
      ...normalizedValues,
    };
  });
};

export class CountryService {
  private static async request<T>(url = ''): Promise<T> {
    try {
      const randomValue8Digits = Math.floor(Math.random() * 100_000_000);
      const currentTime = Date.now();

      const response = await fetch(`${AWS_COUNTRIES_URL}${url}`, {
        headers: { 'X-Request-ID': currentTime + '-' + randomValue8Digits },
      });
      const json: T = await response.json();

      return json;
    } catch (error) {
      console.error(error);
    }
  }

  static async getCountries() {
    const result = await this.request<{ countries: Country[] }>();
    return normalizeCountries(result.countries);
  }

  static getCountryByIsoCode(
    countries: Country[],
    isoCodeTwo: string
  ): Country | undefined {
    return countries.find(
      (country) =>
        country.isoCodeTwo?.toUpperCase() === isoCodeTwo?.toUpperCase()
    );
  }

  static getStateByIsoCode(states: Region[], isoCodeTwo: string) {
    if (!states || !isoCodeTwo) {
      return undefined;
    }

    const upperIsoCode = isoCodeTwo.toUpperCase();
    return states.find(
      (state) => state.isoCodeShort?.toUpperCase() === upperIsoCode
    );
  }

  static getStateByName(states: Region[], name: string) {
    return states.find(
      (state) => state.name.toLowerCase() === name.toLowerCase()
    );
  }

  static convertChinaStateCodeForBackend(
    country: Country | undefined,
    stateCode: string,
    regionName?: string
  ): string {
    if (
      !country ||
      country.isoCodeTwo !== 'CN' ||
      !country.regions ||
      !stateCode
    ) {
      return stateCode;
    }

    if (/^\d+$/.test(stateCode.trim())) {
      return stateCode;
    }

    if (regionName) {
      const region = this.getStateByName(country.regions, regionName);
      if (region?.isoCodeShort) {
        return region.isoCodeShort;
      }
    }
  }

  static convertChinaStateCodeForDisplay(
    country: Country | undefined,
    stateCode: string
  ): string {
    if (
      !country ||
      country.isoCodeTwo !== 'CN' ||
      !country.regions ||
      !stateCode
    ) {
      return stateCode;
    }

    if (!/^\d+$/.test(stateCode.trim())) {
      return stateCode;
    }

    const region = country.regions.find((r) => r.isoCodeShort === stateCode);

    return region?.name || stateCode;
  }
}
